import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { StepBlock } from 'components/StepBlock/StepBlock';
import { Button } from 'components/Button/Button';
import edit from 'assets/images/icons/edit.svg';
import eyeLight from 'assets/images/icons/eye-light.svg';
import trashCan from 'assets/images/icons/trashcan.svg';
import download from 'assets/images/icons/download.svg';
import uploadIcon from 'assets/images/icons/upload.svg';
import successIcon from 'assets/images/icons/success.svg';
import checkIcon from 'assets/images/icons/check.svg';
import { Datalist } from 'components/Datalist/Datalist';
import { CreatePartnerPayload, UpdatePartnerPayload } from 'services/partner/partner.types';
import { AddressAutocomplete } from 'components/Address/Address';
import React from 'react';
import { useSelector } from 'react-redux';
import { getDatalists } from 'features/datalist';
import './PartnerForm.scss';
import { usePartnerFormVM } from './usePartnerFormVM';
import { ApiFile } from 'utils/useFlowTs';
import { downloadFileAsBlob } from 'utils/blob';
import { createColumnHelper } from '@tanstack/react-table';
import Table from 'components/Table/Table';
import { FileUploader } from 'components/FileUploader/FileUploader';
import { Modal } from 'components/Modal/Modal';
import { Document } from 'services/document/document.types';
import { PartnerFormSchema } from './PartnerFormSchema';
import moment from 'moment';
import { ContentBlock } from 'components/ContentBlock/ContentBlock';
import TipTap from 'components/TipTap/TipTap';
interface PartnerFormProps {}

export const PartnerForm: React.FC<PartnerFormProps> = () => {
    const { datalists } = useSelector(getDatalists);
    const [files, setFiles] = useState<ApiFile[]>([]);
    const [localDocuments, setLocalDocuments] = useState<ApiFile[]>([]);
    const [documentModifications, setDocumentModifications] = useState<ApiFile[]>([]);
    const [isDocumentModalOpen, setIsDocumentModalOpen] = useState<boolean>(false);

    const { handlePartnerSubmit, partnerCategories, partner, isLoading } = usePartnerFormVM();

    useEffect(() => {
        if (partner?.documents && partner.documents.length > 0) {
            setLocalDocuments(partner.documents as unknown as ApiFile[]);
        }
    }, [partner]);

    const columnHelper = createColumnHelper<ApiFile>();

    const tableColumns = [
        columnHelper.accessor('label', {
            header: 'Nom du fichier',
            cell: (info) => info.getValue() || `Fichier ${info.row.index + 1}`
        }),
        columnHelper.display({
            header: 'Actions',
            cell: ({ row: { original: currRow } }) => {
                return (
                    <span style={{ display: 'flex', columnGap: 12 }}>
                        <img
                            src={edit}
                            alt="Modifier le document"
                            onClick={() => {
                                setDocumentModifications([currRow]);
                                setIsDocumentModalOpen(true);
                            }}
                        />
                        <img
                            src={eyeLight}
                            alt="Prévisualiser le document"
                            onClick={() => {
                                if (currRow.view_link) {
                                    window.open(currRow.view_link, '_blank');
                                }
                            }}
                        />
                        <img
                            src={trashCan}
                            alt="Supprimer le document"
                            onClick={() => {
                                setLocalDocuments((prevDocs) => prevDocs.filter((doc) => doc.id !== currRow.id));
                            }}
                        />
                        <img
                            src={download}
                            alt="Télécharger le document"
                            onClick={() => {
                                downloadFileAsBlob(currRow).catch((error: any) => {
                                    console.error('Erreur lors du téléchargement:', error);
                                });
                            }}
                        />
                    </span>
                );
            }
        })
    ];

    return (
        <>
            <main id="partner-form">
                <Formik
                    initialValues={{
                        id: partner?.id || 0,
                        category_id: partner?.category_id || 0,
                        name: partner?.name || '',
                        firstname: partner?.firstname || '',
                        lastname: partner?.lastname || '',
                        phone: partner?.phone || '',
                        department: partner?.department || '',
                        role: partner?.role || '',
                        email: partner?.email || '',
                        address: {
                            address: partner?.address?.address || '',
                            address_add: partner?.address?.address_add || '',
                            city: partner?.address?.city || '',
                            zipcode: partner?.address?.zipcode || '',
                            country: partner?.address?.country || ''
                        },
                        source: partner?.source || '',
                        target: partner?.target || '',
                        partner: partner?.partner || '',
                        action: partner?.action || '',
                        contacted_at: partner?.contacted_at || '',
                        met_at: partner?.met_at || '',
                        documents: partner?.documents || [],
                        observation: partner?.observation || ''
                    }}
                    enableReinitialize
                    validationSchema={() => PartnerFormSchema(datalists || {})}
                    onSubmit={async (values, { setSubmitting }) => {
                        await handlePartnerSubmit(values);
                        setSubmitting(false);
                    }}
                >
                    {({ values, touched, errors, handleChange, handleBlur, handleSubmit, setFieldValue }) => {
                        function handleFileUploadSuccess(file: ApiFile) {
                            setLocalDocuments((prevDocs) => [...prevDocs, file]);
                            setFieldValue('documents', [...(values.documents || []), file]);
                        }

                        const formatDateForInput = (dateString: string | null | undefined): string => {
                            if (!dateString) return '';
                            return moment(dateString).format('YYYY-MM-DD');
                        };

                        return (
                            <>
                                <form onSubmit={handleSubmit} className="new-partner form">
                                    <ContentBlock isLoading={isLoading} title="Nom du partenaire" className="name">
                                        <div className="form-line">
                                            <div className="form-group">
                                                {/* <label>Nom du partenaire*</label> */}
                                                <input
                                                    type="text"
                                                    name="name"
                                                    placeholder="Entrez ici le nom du partenaire"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.name}
                                                    required
                                                />
                                                {errors.name && touched.name && <p className="error">{errors.name}</p>}
                                            </div>
                                        </div>
                                    </ContentBlock>
                                    <ContentBlock isLoading={isLoading} title="Informations concernant le partenaire" className="infos">
                                        <div className="form-line">
                                            <div className="form-group">
                                                <label>Catégorie du partenaire*</label>
                                                <select
                                                    name="category_id"
                                                    onChange={(e) => {
                                                        setFieldValue('category_id', Number(e.target.value));
                                                    }}
                                                    onBlur={handleBlur}
                                                    value={values.category_id}
                                                    required
                                                >
                                                    <option key={0} value={0}>
                                                        Sélectionnez la catégorie du partenaire
                                                    </option>
                                                    {partnerCategories
                                                        .filter((partnerCategory) => partnerCategory.id !== 0)
                                                        .map((partnerCategory) => (
                                                            <option key={partnerCategory.id} value={partnerCategory.id}>
                                                                {partnerCategory.label}
                                                            </option>
                                                        ))}
                                                </select>
                                                {errors.category_id && touched.category_id && <p className="error">{errors.category_id}</p>}
                                            </div>

                                            <div className="form-group">
                                                <label>Département</label>
                                                <Datalist
                                                    list="departments"
                                                    name="department"
                                                    placeholder="Sélectionnez un département"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.department}
                                                />
                                                {errors.department && touched.department && <p className="error">{errors.department}</p>}
                                            </div>
                                        </div>

                                        <div className="form-line three-children">
                                            <div className="form-group">
                                                <label>Prénom de l'interlocuteur</label>
                                                <input
                                                    type="text"
                                                    name="firstname"
                                                    placeholder="Entrez ici le prénom de l'interlocuteur"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.firstname}
                                                />
                                                {errors.firstname && touched.firstname && <p className="error">{errors.firstname}</p>}
                                            </div>

                                            <div className="form-group">
                                                <label>Nom de l'interlocuteur</label>
                                                <input
                                                    type="text"
                                                    name="lastname"
                                                    placeholder="Entrez ici le nom de l'interlocuteur"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.lastname}
                                                />
                                                {errors.lastname && touched.lastname && <p className="error">{errors.lastname}</p>}
                                            </div>
                                            <div className="form-group">
                                                <label>Fonction de l'interlocuteur</label>
                                                <input
                                                    type="text"
                                                    name="role"
                                                    placeholder="Entrez ici la fonction de l'interlocuteur"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.role}
                                                />
                                                {errors.role && touched.role && <p className="error">{errors.role}</p>}
                                            </div>
                                        </div>

                                        <div className="form-line three-children">
                                            <div className="form-group">
                                                <label>Public concerné</label>
                                                <Datalist
                                                    list="partner_targets"
                                                    name="target"
                                                    placeholder="Sélectionnez un public concerné"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.target}
                                                />
                                                {errors.target && touched.target && <p className="error">{errors.target}</p>}
                                            </div>

                                            <div className="form-group">
                                                <label>Action partenariale envisagée</label>
                                                <Datalist
                                                    list="partner_actions"
                                                    name="action"
                                                    placeholder="Sélectionnez une action partenariale"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.action}
                                                />
                                                {errors.action && touched.action && <p className="error">{errors.action}</p>}
                                            </div>
                                        </div>
                                    </ContentBlock>
                                    <ContentBlock isLoading={isLoading} title="Coordonnées du partenaire" className="contact-infos">
                                        <div className="form-line">
                                            <div className="form-group">
                                                <label>Numéro de téléphone*</label>
                                                <input
                                                    type="text"
                                                    name="phone"
                                                    placeholder="Entrez ici le numéro de téléphone du partenaire"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.phone}
                                                />
                                                {errors.phone && touched.phone && <p className="error">{errors.phone}</p>}
                                            </div>

                                            <div className="form-group">
                                                <label>Adresse e-mail</label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    placeholder="Entrez ici l'adresse e-mail du partenaire"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.email}
                                                />
                                                {errors.email && touched.email && <p className="error">{errors.email}</p>}
                                            </div>
                                        </div>

                                        <div className="form-line">
                                            <div className="form-group">
                                                <label>Adresse postale</label>
                                                <div className="fields address">
                                                    <AddressAutocomplete
                                                        baseField="address"
                                                        formValue={values.address}
                                                        setValue={setFieldValue}
                                                    />
                                                </div>
                                                {/* {errors.address && touched.address && <p className="error">{errors.address}</p>} */}
                                            </div>
                                        </div>
                                    </ContentBlock>
                                    <ContentBlock isLoading={isLoading} title="Prise de contact" className="contact-source">
                                        <div className="form-line">
                                            <div className="form-group">
                                                <label>Source du contact</label>
                                                <Datalist
                                                    list="partner_sources"
                                                    name="source"
                                                    placeholder="Sélectionnez une source"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.source}
                                                />
                                                {errors.source && touched.source && <p className="error">{errors.source}</p>}
                                            </div>

                                            <div className="form-group">
                                                <label>Date de prise de contact</label>
                                                <input
                                                    type="date"
                                                    name="contacted_at"
                                                    placeholder="Entrez ici la date de prise de contact"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={formatDateForInput(values.contacted_at)}
                                                />
                                                {errors.contacted_at && touched.contacted_at && (
                                                    <p className="error">{errors.contacted_at}</p>
                                                )}
                                            </div>

                                            <div className="form-group">
                                                <label>Date de rencontre</label>
                                                <input
                                                    type="date"
                                                    name="met_at"
                                                    placeholder="Entrez ici la date de rencontre"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={formatDateForInput(values.met_at)}
                                                />
                                                {errors.met_at && touched.met_at && <p className="error">{errors.met_at}</p>}
                                            </div>
                                        </div>
                                    </ContentBlock>
                                    <ContentBlock isLoading={isLoading} title="Documents rattachés au partenaire" className="documents">
                                        {localDocuments && localDocuments.length > 0 ? (
                                            <Table data={localDocuments} columns={tableColumns} columnHelper={columnHelper} selectable />
                                        ) : (
                                            <FileUploader
                                                files={files}
                                                setFiles={setFiles}
                                                onSuccess={handleFileUploadSuccess}
                                                size="big"
                                            />
                                        )}
                                        {localDocuments && localDocuments.length > 0 && (
                                            <FileUploader
                                                files={files}
                                                setFiles={setFiles}
                                                onSuccess={handleFileUploadSuccess}
                                                label="Importer un document supplémentaire"
                                                variant="light"
                                                icon={<img src={uploadIcon} alt="Importer un document supplémentaire" />}
                                            />
                                        )}
                                        <p className="accepted-file">
                                            Types de fichiers acceptés : pdf, docx, doc, xlsx, xls, pptx, ppt, txt, csv, jpg, jpeg, png,
                                            gif.
                                        </p>
                                    </ContentBlock>
                                    <ContentBlock isLoading={isLoading} title="Commentaires" className="observations">
                                        <div className="form-line">
                                            <div className="form-group">
                                                <div className="wysiwyg-container">
                                                    <TipTap
                                                        name="observation"
                                                        value={values.observation}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                    />
                                                </div>
                                                {errors.observation && <p className="error">{errors.observation}</p>}
                                            </div>
                                        </div>
                                    </ContentBlock>
                                    <div className="actions-buttons">
                                        <Button
                                            type="submit"
                                            label={partner ? 'Modifier le partenaire' : 'Ajouter le partenaire'}
                                            icon={<img src={checkIcon} alt="Ajouter le partenaire" />}
                                            iconAfter
                                        />
                                    </div>
                                </form>
                                <Modal
                                    open={isDocumentModalOpen}
                                    setClose={() => {
                                        setIsDocumentModalOpen(false);
                                        setDocumentModifications([]);
                                    }}
                                    title="Modifier les informations du document"
                                    yesText="Modifier"
                                    noText="Annuler"
                                    onValidate={() => {
                                        setLocalDocuments((prev) =>
                                            prev.map((doc) => (doc.id === documentModifications[0]?.id ? documentModifications[0] : doc))
                                        );
                                        setFieldValue(
                                            'documents',
                                            values.documents?.map((doc: File | Document) =>
                                                'id' in doc && doc.id === documentModifications[0]?.id ? documentModifications[0] : doc
                                            )
                                        );
                                        setIsDocumentModalOpen(false);
                                        setDocumentModifications([]);
                                    }}
                                    onClose={() => {
                                        setIsDocumentModalOpen(false);
                                        setDocumentModifications([]);
                                    }}
                                >
                                    <div className="form">
                                        <div className="form-line">
                                            <div className="form-group">
                                                <label>Nom du fichier</label>
                                                <input
                                                    type="text"
                                                    name="label"
                                                    onChange={(e) => {
                                                        const newLabel = e.target.value;
                                                        setDocumentModifications((prev) => {
                                                            const updatedModifications = [...prev];
                                                            if (updatedModifications[0]) {
                                                                updatedModifications[0] = {
                                                                    ...updatedModifications[0],
                                                                    label: newLabel
                                                                };
                                                            }
                                                            return updatedModifications;
                                                        });
                                                    }}
                                                    onBlur={handleBlur}
                                                    value={documentModifications[0]?.label || ''}
                                                />
                                                {typeof errors.documents === 'object' && (errors.documents as any).label && (
                                                    <p className="error">{(errors.documents as any).label}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-line">
                                            <div className="form-group">
                                                <label>Description</label>
                                                <textarea
                                                    name="observation"
                                                    onChange={(e) => {
                                                        const newObservation = e.target.value;
                                                        setDocumentModifications((prev) => {
                                                            const updatedModifications = [...prev];
                                                            if (updatedModifications[0]) {
                                                                updatedModifications[0] = {
                                                                    ...updatedModifications[0],
                                                                    observation: newObservation
                                                                };
                                                            }
                                                            return updatedModifications;
                                                        });
                                                    }}
                                                    onBlur={handleBlur}
                                                    value={documentModifications[0]?.observation || ''}
                                                    rows={6}
                                                />
                                                {typeof errors.documents === 'object' && (errors.documents as any).observation && (
                                                    <p className="error">{(errors.documents as any).observation}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </Modal>
                            </>
                        );
                    }}
                </Formik>
            </main>
        </>
    );
};
